import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth-context";

interface SyncHealthStats {
  unprocessedCount: number;
  processedCount: number;
  lastAcknowledgedAt: string | null;
  oldestUnprocessedAt: string | null;
  status: "healthy" | "warning" | "critical";
  oldestUnprocessedAgeMinutes: number;
}

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  healthy: { color: "bg-emerald-400", label: "Sync: Healthy" },
  warning: { color: "bg-amber-400", label: "Sync: Queue growing" },
  critical: { color: "bg-red-400", label: "Sync: Not responding" },
  unknown: { color: "bg-gray-500", label: "Sync: Loading..." },
};

export function SyncHealthDot() {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery<SyncHealthStats>({
    queryKey: ["/api/admin/sync/health"],
    refetchInterval: 60000,
    enabled: user?.role === "admin",
  });

  if (user?.role !== "admin") return null;

  const status = isLoading || error || !data ? "unknown" : data.status;
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;

  const tooltip = data && status !== "healthy"
    ? `${config.label} (${data.unprocessedCount} pending, oldest ${data.oldestUnprocessedAgeMinutes}m ago)`
    : config.label;

  return (
    <div className="relative group" title={tooltip}>
      <div className={`w-2 h-2 rounded-full ${config.color}`} />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
        {tooltip}
      </div>
    </div>
  );
}
